/**
 * Back-in-stock subscribe button (Klaviyo), product pages.
 *
 * Rendered by snippets/back-in-stock-button.liquid directly after each template's
 * add-to-cart button. When the selected variant is unavailable AND Klaviyo is
 * reachable, the disabled ATC is hidden and this button shows in its place; when
 * the variant is available (or Klaviyo never loads — fallback: keep the theme's
 * disabled sold-out button, never a dead control) the ATC returns.
 *
 * Variant switching: every hero dispatches `on:variant:change` on document with
 * detail.variant = { id, available, ... } — re-evaluated on each change.
 *
 * Two click paths:
 *  - logged-in (email rendered onto the button): direct client-side subscribe via
 *    Klaviyo's back-in-stock API. No modal; button swaps to a confirmed state.
 *  - logged out: opens Klaviyo form (data-bis-form-id), which handles capture.
 *
 * Confirmed state is remembered per-variant for the session (sessionStorage).
 * No dedupe checks — Klaviyo dedupes server-side.
 */
/* Stock-pill class sync — product-inventory.js swaps the pill's TEXT on variant
   change but doesn't know each template's class prefix (hp-/rod-/reel-/lure-pill),
   so without this the pill stays green while reading "Out of stock". hero-product
   and cooler carry their own inline copy of this; the generic prefix detection
   makes doubling up harmless. Lives here because this file is loaded by every
   product template via the back-in-stock snippet. */
(function () {
  var notice = document.querySelector('.js-inventory-notice');
  if (!notice) return;
  var stockCls = null;
  notice.classList.forEach(function (c) {
    if (/-pill--stock$/.test(c)) stockCls = c;
  });
  if (!stockCls) return;
  var outCls = stockCls.replace('--stock', '--out');
  function syncPill() {
    var isOos = notice.textContent.trim().toLowerCase().indexOf('out') !== -1;
    notice.classList.toggle(stockCls, !isOos);
    notice.classList.toggle(outCls, isOos);
  }
  syncPill();
  new MutationObserver(syncPill).observe(notice, { childList: true, characterData: true, subtree: true });
})();

(function () {
  var bisBtn = document.querySelector('[data-bis-btn]');
  if (!bisBtn) return;
  var atcBtn = document.querySelector(bisBtn.dataset.atcSelector || '[data-bis-nothing]');
  if (!atcBtn) return;

  var companyId = bisBtn.dataset.companyId;
  var formId = bisBtn.dataset.formId;
  var email = bisBtn.dataset.customerEmail || '';
  var productUrl = bisBtn.dataset.productUrl || '';
  var variantImages = {};
  (function () {
    var el = document.querySelector('[data-bis-images]');
    if (el) { try { variantImages = JSON.parse(el.textContent || '{}'); } catch (e) {} }
  })();
  // Klaviyo may reject (400) or silently drop custom attributes on this endpoint;
  // on a 400 we retry without them and remember, so the core subscribe never breaks.
  var customPropsSupported = true;
  var LABEL = "EMAIL ME WHEN IT'S BACK";
  var LABEL_DONE = "We'll let you know ✓";
  var LABEL_BUSY = "Adding…";
  var inFlight = false;

  var state = {
    id: bisBtn.dataset.initialVariant || '',
    available: bisBtn.dataset.initialAvailable === 'true'
  };

  function storageKey(id) { return 'bis:' + id; }
  function isConfirmed(id) {
    try { return sessionStorage.getItem(storageKey(id)) === '1'; } catch (e) { return false; }
  }
  function setConfirmed(id) {
    try { sessionStorage.setItem(storageKey(id), '1'); } catch (e) {}
  }

  // Klaviyo onsite is injected async by the app embed; treat it as ready when either
  // the modern or legacy hook exists. Until then the theme's disabled sold-out
  // button stays — never a dead control.
  function klaviyoReady() {
    return !!(window.klaviyo || window._klOnsite || window._learnq);
  }

  function render() {
    var showBis = !state.available && !!state.id && klaviyoReady();
    // Inline display (not [hidden]) — the templates' ATC classes set display:block,
    // which would override the hidden attribute.
    atcBtn.style.display = showBis ? 'none' : '';
    bisBtn.hidden = !showBis;
    if (!showBis) return;
    if (isConfirmed(state.id)) {
      bisBtn.classList.add('bis-btn--done');
      bisBtn.textContent = LABEL_DONE;
      bisBtn.disabled = true;
    } else {
      bisBtn.classList.remove('bis-btn--done');
      bisBtn.textContent = inFlight ? LABEL_BUSY : LABEL;
      bisBtn.disabled = inFlight;
    }
  }

  function openForm() {
    window._klOnsite = window._klOnsite || [];
    window._klOnsite.push(['openForm', formId]);
  }

  // Client-side endpoint: public key in the query string, NO Authorization header
  // (a private key here would fail CORS and leak the key). Success is a 202 with an
  // EMPTY body — never call .json() on it, check response.ok only.
  function subscribeDirect(variantId, withProps) {
    var attributes = {
      channels: ['EMAIL'],
      profile: { data: { type: 'profile', attributes: { email: email } } }
    };
    if (withProps) {
      // Custom properties for the back-in-stock email: exact-variant URL (the
      // ?variant= param matters — landing on the wrong preselected variant is how
      // these emails lose sales) and the variant's image.
      attributes.ProductURL = productUrl + '?variant=' + variantId;
      var img = variantImages[variantId];
      if (img) attributes.ProductImage = img;
    }
    return fetch('https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=' + encodeURIComponent(companyId), {
      method: 'POST',
      headers: { 'Content-Type': 'application/vnd.api+json', 'revision': '2025-07-15' },
      body: JSON.stringify({
        data: {
          type: 'back-in-stock-subscription',
          attributes: attributes,
          relationships: {
            // The $shopify:::$default::: prefix is load-bearing: a bare Shopify ID
            // returns 202 success and notifies NOBODY. Variant-level, never product.
            variant: { data: { type: 'catalog-variant', id: '$shopify:::$default:::' + variantId } }
          }
        }
      })
    }).then(function (res) {
      if (res.ok) return;
      // Strict schema rejection of the custom props — retry bare so the core
      // subscribe still lands, and stop sending props for the session.
      if (withProps && res.status === 400) {
        customPropsSupported = false;
        return subscribeDirect(variantId, false);
      }
      throw new Error('bis subscribe failed: ' + res.status);
    });
  }

  // Brief inline error note under the button — shown when the direct subscribe
  // fails; the button reverts to its default state (never stuck in flight).
  var errEl = null;
  var errTimer = null;
  function showError() {
    if (!errEl) {
      errEl = document.createElement('p');
      errEl.className = 'bis-error';
      bisBtn.insertAdjacentElement('afterend', errEl);
    }
    errEl.textContent = "Couldn't sign you up — please try again.";
    errEl.hidden = false;
    clearTimeout(errTimer);
    errTimer = setTimeout(function () { errEl.hidden = true; }, 5000);
  }

  bisBtn.addEventListener('click', function () {
    if (bisBtn.disabled || inFlight || !state.id) return;
    var variantId = state.id;
    if (email) {
      inFlight = true;
      bisBtn.disabled = true;
      bisBtn.textContent = LABEL_BUSY;
      subscribeDirect(variantId, customPropsSupported)
        .then(function () {
          inFlight = false;
          setConfirmed(variantId);
          render();
        })
        .catch(function () {
          inFlight = false;
          render(); // revert to default state for the current variant
          showError();
        });
    } else {
      openForm();
    }
  });

  document.addEventListener('on:variant:change', function (evt) {
    var v = evt.detail && evt.detail.variant;
    if (v && v.id) {
      state.id = String(v.id);
      state.available = !!v.available;
    } else if (evt.detail && evt.detail.variant === null) {
      // Non-existent option combo — nothing to subscribe to.
      state.available = true;
    }
    render();
  });

  // klaviyo.js loads async: poll briefly so an initially sold-out variant swaps to
  // the BIS button once Klaviyo appears (bounded — if it never loads, the theme's
  // disabled sold-out button simply remains).
  render();
  if (!klaviyoReady()) {
    var tries = 0;
    var t = setInterval(function () {
      if (klaviyoReady() || ++tries > 40) {
        clearInterval(t);
        render();
      }
    }, 250);
  }
})();

/* Elsewhere-price info icon — each hero's variant-change JS rewrites the compare
   label between "Elsewhere:" and "Was:" but doesn't know about the icon, so we
   watch the label's text instead of patching seven templates. The icon lives
   inside the compare-wrap, so the wrap hiding (no compare at all) hides it too;
   this only has to handle the Elsewhere→Was case. Same observer pattern as the
   stock-pill sync above. */
(function () {
  var infos = document.querySelectorAll('[data-els-info]');
  if (!infos.length) return;
  infos.forEach(function (info) {
    var wrap = info.parentElement;
    var label = wrap && wrap.querySelector('[data-hp-compare-label], [data-reel-compare-label], [data-rod-compare-label], [data-compare-label]');
    if (!label) return;
    function sync() {
      info.style.display = label.textContent.indexOf('Elsewhere') === 0 ? '' : 'none';
    }
    sync();
    new MutationObserver(sync).observe(label, { childList: true, characterData: true, subtree: true });
  });
})();

/* Wishlist placeholder bridge — the Klaviyo Customer Hub widget hydrates after page
   load, which made the favorites control pop in late. A theme-rendered placeholder
   heart (outline; filled once saved) paints instantly; this watches the slot, flips to the real widget the moment
   it mounts, and forwards any click made during the gap. */
(function () {
  var slot = document.querySelector('.hp-atc-wishlist');
  if (!slot) return;
  var ph = slot.querySelector('[data-wish-ph]');
  if (!ph) return;
  var queued = false;
  // Klaviyo's control isn't guaranteed to be a <button>/<a> (it can be div-based),
  // so prefer interactive elements but fall back to any non-placeholder child that
  // has rendered content (an svg or text) — clicks on it reach their delegated
  // handlers either way.
  // Klaviyo renders <klaviyo-favorites-button> with shadow DOM. The TAG exists in
  // the server HTML before hydration, so presence alone isn't readiness — swapping
  // on it produced placeholder -> blank gap -> widget. Ready = the host has actually
  // PAINTED content (offsetHeight, or a populated shadow root when accessible).
  function widgetTarget() {
    return slot.querySelector('klaviyo-favorites-button');
  }
  function widgetPainted(host) {
    if (!host) return false;
    if (host.shadowRoot && host.shadowRoot.childElementCount > 0) return true;
    return host.offsetHeight > 8;
  }
  // Saved state (2026-10-11): the theme heart fills when Klaviyo's (invisible) control
  // reports the product is already a favourite. Klaviyo marks its icon span with
  // kl-hub-wishlist-icon--active / --inactive and labels the button "Remove from
  // favorites"; either signal flips .is-saved on the slot.
  var observing = false;
  function syncSaved() {
    var b = widgetTarget();
    if (!b) return;
    var saved = !!b.querySelector('.kl-hub-wishlist-icon--active') ||
      /remove/i.test((b.textContent || ''));
    slot.classList.toggle('is-saved', saved);
    if (ph) ph.setAttribute('aria-pressed', saved ? 'true' : 'false');
  }
  function observeSaved() {
    if (observing) return;
    var b = widgetTarget();
    if (!b || !window.MutationObserver) return;
    observing = true;
    new MutationObserver(syncSaved).observe(b, { childList: true, subtree: true, attributes: true, characterData: true });
    syncSaved();
  }
  function check() {
    var b = widgetTarget();
    if (!widgetPainted(b)) return false;
    slot.classList.add('wish-ready');
    observeSaved();
    if (queued) { queued = false; forward(b); }
    return true;
  }
  // 2026-10-11: the placeholder is the click target for the whole 48px slot (Klaviyo's own
  // button only covers 62x40 of it); every click is forwarded to Klaviyo's real button.
  function forward(host) {
    var btn = host && (host.querySelector('button, [role="button"], a') || host);
    if (btn) btn.click();
  }
  ph.addEventListener('click', function () {
    if (check()) forward(widgetTarget()); else queued = true;
  });
  if (!check()) {
    // Shadow-DOM mutations are invisible to a light-DOM observer, so poll for the
    // painted state (bounded); customElements.whenDefined narrows the window.
    var tries = 0;
    var t = setInterval(function () {
      if (check() || ++tries > 100) clearInterval(t);
    }, 150);
    if (window.customElements && customElements.whenDefined) {
      try { customElements.whenDefined('klaviyo-favorites-button').then(function () { setTimeout(check, 50); }); } catch (e) {}
    }
  }
})();
